/**
 * Tenant agent catalog — browse published OOTB agents and deploy instances (WF-F6).
 * Platform Admin factory authoring stays on `/ai-studio/agents`.
 */

import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CubeTransparentIcon } from '@heroicons/react/24/outline';
import { FlexibleCRUDTable } from '@/components/common/FlexibleCRUDTable';
import { aiStudioAgentEntity } from '@/config/entities/aiStudioAgents';
import { aiStudioAgentsMarketplaceCRUDService } from '@/services/aiStudioAgentsMarketplaceCRUD';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AiStudioAgent } from '@/types/aiStudioAgent';

const AIStudioAgentMarketplacePage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [errorKey, setErrorKey] = useState<string | null>(null);

  const handleDeploy = useCallback(
    (agent: AiStudioAgent) => {
      setErrorKey(null);
      navigate(
        `/ai-studio/instances/provision?agentId=${encodeURIComponent(agent.id)}`
      );
    },
    [navigate]
  );

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header>
        <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.catalog.title')}
        </h1>
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.catalog.subtitle')}
        </p>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(errorKey)}
        </div>
      )}

      <FlexibleCRUDTable
        entity={aiStudioAgentEntity}
        service={aiStudioAgentsMarketplaceCRUDService}
        showCreateButton={false}
        showSearch
        showPagination
        pageSize={20}
        searchPlaceholder={t('aiStudio.agents.searchLabel')}
        canEdit={() => false}
        canCopy={() => false}
        canDelete={() => false}
        customActions={[
          {
            label: t('aiStudio.instances.deployInstance'),
            icon: CubeTransparentIcon,
            variant: 'secondary',
            visible: () => true,
            onClick: (agent: AiStudioAgent) => {
              handleDeploy(agent);
            },
          },
        ]}
        onRowClick={(agent: AiStudioAgent) => {
          handleDeploy(agent);
        }}
      />
    </main>
  );
};

export default AIStudioAgentMarketplacePage;
